/**
 * Component Resources
 *
 * Provides access to component catalog data.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { ResourceTemplate } from '@modelcontextprotocol/sdk/server/mcp.js';
import componentCatalog from '../data/component-catalog.json' with { type: 'json' };
import type { ComponentCatalog } from '../types.js';

const catalog = componentCatalog as ComponentCatalog;

/**
 * Register component-related resources
 */
export function registerComponentResources(server: McpServer): void {
  // Full component catalog
  server.resource('Component Catalog', 'components://catalog', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog, null, 2),
      },
    ],
  }));

  // Component list (names only, for quick lookup)
  server.resource('Component List', 'components://list', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          catalog.components.map((c) => ({
            name: c.name,
            category: c.category,
            description: c.description,
          })),
          null,
          2
        ),
      },
    ],
  }));

  // Individual component resources via template
  server.resource(
    'Component Details',
    new ResourceTemplate('components://{name}', { list: undefined }),
    async (uri, params) => {
      const name = params.name as string;
      const component = catalog.components.find((c) => c.name.toLowerCase() === name.toLowerCase());

      if (!component) {
        return {
          contents: [
            {
              uri: uri.href,
              mimeType: 'application/json',
              text: JSON.stringify({ error: `Component "${name}" not found` }),
            },
          ],
        };
      }

      return {
        contents: [
          {
            uri: uri.href,
            mimeType: 'application/json',
            text: JSON.stringify(component, null, 2),
          },
        ],
      };
    }
  );

  // Components by category
  server.resource(
    'Components by Category',
    new ResourceTemplate('components://category/{category}', { list: undefined }),
    async (uri, params) => {
      const category = params.category as string;
      const components = catalog.components.filter(
        (c) => c.category.toLowerCase() === category.toLowerCase()
      );

      return {
        contents: [
          {
            uri: uri.href,
            mimeType: 'application/json',
            text: JSON.stringify(components, null, 2),
          },
        ],
      };
    }
  );
}
